const swea = require('swea');
const ImageViewerServiceActions = swea.react.actions.ImageViewerServiceActions;
const ImageViewerService = swea.react.services.ImageViewerService;
const ViewerManager = swea.editor.ViewerManager;
const InspectionAdapter = swea.editor.InspectionAdapter;
const ImageCatalogueServiceActions = swea.react.actions.ImageCatalogueServiceActions;
const ImageCatalogueService = swea.react.services.ImageCatalogueService;
const InstructionServiceActions = swea.react.actions.InstructionServiceActions;
const ImageSelectionInteraction = swea.editor.interactions.ImageSelectionInteraction;
const FavoriteService = swea.react.services.FavoriteService;
const BrowserInfo = swea.react.helpers.BrowserInfo;
const MeasureService = swea.react.services.MeasureService;

/**
 * Basic example of consecutive interactions.
 */
class CustomImageSelectionInteraction extends ImageSelectionInteraction {

  /**
   * The constructor.
   * @param { object } options The options described in ImageSelectionInteraction with the following additions:
   * @param { boolean } options.reopen (optional) True to reopen image catalog after closing image view. Default is true.
   * @param { boolean } options.showDownload (optional) False to disable download image icon. Default is true.
   * @param { boolean } options.showOrthogonalGsd (optional) True to measure and show the orthogonal GSD, if any.
   * @param { string } options.orthogonalGsdTitle (optional) The orthogonal GSD title, default is "Orthogonal GSD".
   * @param { boolean } options.forceOverlayViewer (optional) True to force setting overlay viewer even if mobile, to update e.g. compass and altitude. Default is false.
   */
  constructor(options) {
    super(options);
    this._reopen = (options.reopen === undefined ? true : options.reopen);
    this._onImageViewerChangeListener = undefined;
    this._imageCatalogue= undefined;
    this._showDownload = (options.showDownload === undefined ? true : options.showDownload);
    this._showOrthogonalGsd = (options.showOrthogonalGsd === undefined ? false : options.showOrthogonalGsd);
    this._orthogonalGsdTitle = (options.orthogonalGsdTitle === undefined ? 'Orthogonal GSD' : options.orthogonalGsdTitle);
    this._forceOverlayViewer = (options.forceOverlayViewer === undefined ? false : options.forceOverlayViewer);
    this._inspectionPoint = undefined;
  }
   
  /**
   * Initializes the test interaction.
   * @param element
   * @param viewerId
   */
  initialize(element, viewerId) {
    super.initialize( element, viewerId );
    this._reopen = true;
    this._smart = false;
    this._smartNavigationImages = undefined;
  }

  /**
   * Sets the inspection point to be able to highligt it in image view.
   * @param { number[] } inspectionPoint The inspection point in world coordinates as an array of x,y,z in meters.
   * @public
   */
  setInspectionPoint(inspectionPoint) {
    this._inspectionPoint = inspectionPoint;
  }

  /**
   * Sets whether overlay viewer should be set even if mobile, to update e.g. compass and altitude.
   * @param { boolean } forceOverlayViewer True to force setting overlay viewer.
   * @public
   */
  setForceOverlayViewer(forceOverlayViewer) {
    this._forceOverlayViewer = forceOverlayViewer;
  }
  
  /**
   * Sets wether to reopen image catalog after closing image view or not.
   * @param { boolean } reopen True to activate reopening of image catalog after closing image view.
   * public
   */
  setReopen(reopen) {
    this._reopen = reopen;
  }
  
  /**
   * Returns wether to reopen image catalog after closing image view or not.
   * @returns { boolean } True to activate reopening of image catalog after closing image view.
   * public
   */
  getReopen() {
    return this._reopen;
  }

  /**
   * Enables smart navigation from chosen image.
   * @param { boolean } smart True for smart navigation.
   * @param { string[] } images A list of image names for smart navigation.
   * @public
   */
  setSmart(smart, images) {
    this._smart = smart;
    this._smartNavigationImages = images;
  }

  /**
   * When an image is selected we want to view it in the ImageViewer.
   * @param { string } imageName The name of the selected image.
   * @override
   */
  whenImageSelected = (imageName) => {
    if (this._imageCatalogue === undefined) {
      return;
    }
    
    ImageCatalogueServiceActions.close();
    const imageNames = this._imageCatalogue.cameras.map((camera) => {
      return camera.name;
    });
    const state = FavoriteService.instance().getState();

    let filteredImageNames = imageNames;
    if(state && state.filterFavorites) {
      filteredImageNames = imageNames.filter(function(name){
        return state.favorites.indexOf(name) !== -1;
      });
    }

    // Shift the array so the selected image is first
    let index = 0;
    const filteredImagesLength = filteredImageNames.length;
    for(let i = 0; i < filteredImagesLength; i++){
      if(filteredImageNames[i] === imageName){
        index = i;
        break;
      }
    }
    const left = filteredImageNames.splice(0, index);
    filteredImageNames = filteredImageNames.concat(left);

    const overlayViewer = (BrowserInfo.isMobile() && !this._forceOverlayViewer ? undefined : ViewerManager.instance().getViewerById(this._viewerId));
    
    let smartNavigationImages = undefined;
    if (this._smart && this._smartNavigationImages !== undefined) {
      // Add chosen image temporarily to smart navigation images
      smartNavigationImages = this._smartNavigationImages.slice();
      smartNavigationImages.push(imageName);
    }
    
    ImageViewerServiceActions.open({
      imageNames: filteredImageNames,
      overlayViewer: overlayViewer,
      showDownload: this._showDownload,
      showOrthogonalGsd: this._showOrthogonalGsd,
      orthogonalGsdTitle: this._orthogonalGsdTitle,
      enableFavorites: this._enableFavorites,
      smartNavigation: this._smart,
      smartNavigationImages: smartNavigationImages,
      sameFolder: false,
      arrowButtons: !this._smart,
      highlightPoint: this._inspectionPoint
    });
    InstructionServiceActions.showInstruction('instructions.image_viewer');

    // Reset smart for next time
    this._smart = false;
    this._smartNavigationImages = undefined;
  };

  /**
   * Opens an image catalogue with the images from the image Inspection Adapter.
   * @param { object } catalogue The image catalogue as an object described below:
   * @param { object[] } catalogue.cameras A list of camera objects containing name, extrinsics, size, intrinsics and distorsion.
   * @override
   */
  open(imageCatalogue) {
    this._onImageViewerChangeListener = ImageViewerService.instance().addChangeListener(this._onImageViewerChange);
    this._onImageCatalogueChangeListener = ImageCatalogueService.instance().addChangeListener(this._onImageCatalogueChange);
    if (imageCatalogue) {
      this._imageCatalogue = imageCatalogue;
    }
    else {
      this._imageCatalogue = {
        cameras: InspectionAdapter.getCameras()
      };
    }

    super.open(this._imageCatalogue);
    InstructionServiceActions.hideInstruction();
  }

  /**
   * Closes the image catalogue.
   * @override
   */
  close(){
    if (this._onImageViewerChangeListener !== undefined) {
      ImageViewerService.instance().removeChangeListener(this._onImageViewerChangeListener);
      this._onImageViewerChangeListener = undefined;
    }
    if (this._onImageCatalogueChangeListener !== undefined) {
      ImageCatalogueService.instance().removeChangeListener(this._onImageCatalogueChangeListener);
      this._onImageCatalogueChangeListener = undefined;
    }
    ImageViewerServiceActions.close();
    super.close();
  }

  /**
   * When the image viewer component is closed we want to open the catalog again
   * this is taken care of here.
   * @private
   */
  _onImageViewerChange = () => {
    const newState = ImageViewerService.instance().getState();
    if (this._reopen && (this._state === undefined || (this._state.isOpen && !newState.isOpen)) && !MeasureService.instance().isRefining()) {
      // When the ImageViewer is closed, re-open the image catalogue
      this.open(this._imageCatalogue);
    }
    this._state = newState;
  };

  /**
   * Sets new catalogue if version has changed.
   * @private
   */
  _onImageCatalogueChange = () => {
    const newCatalogue = ImageCatalogueService.instance().getState().catalogue;
    if (newCatalogue !== undefined && newCatalogue.cameras !== undefined && newCatalogue.cameras.length > 0) {
      this._imageCatalogue = newCatalogue;
    }
  };

  whenTerminated(){
    super.whenTerminated();
    if (this._onImageViewerChangeListener !== undefined) {
      ImageViewerService.instance().removeChangeListener(this._onImageViewerChangeListener);
      this._onImageViewerChangeListener = undefined;
    }
  }
}
export default CustomImageSelectionInteraction;
